import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AuthLayout from "../components/layout/AuthLayout";
import AgentSelect from "../components/common/AgentSelect";
import Button from "../components/common/Button";
import OtpInputs from "../components/common/OtpInputs";
import {
  registerAgent,
  resendAgentRegistrationOtp,
  sendAgentRegistrationOtp,
  verifyAgentRegistrationOtp,
} from "../lib/agentAuth";
import { BUSINESS_TYPES, formatOtpCountdown } from "../lib/agentOnboarding";
import { showApiError, showErrorAction, showSuccessAction } from "../lib/alerts";
import { getApiErrorMessage, getErrorMessage } from "../lib/apiMessages";
import { BRAND } from "../lib/brand";
import { fetchCities, fetchCountries, fetchStates, findLocationLabel } from "../lib/locations";

const emailPattern = /^\S+@\S+\.\S+$/;
const mobilePattern = /^[0-9+\-\s]{8,15}$/;

const stepLabels = ["Agency Details", "Agent OTP", "Business Information", "Account Security", "Review & Submit"];

const initialValues = {
  agencyName: "",
  ownerName: "",
  email: "",
  mobile: "",
  countryId: "",
  stateId: "",
  cityId: "",
  address: "",
  businessType: "",
  password: "",
  confirmPassword: "",
  terms: false,
};

const stepFields = {
  1: ["agencyName", "ownerName", "email", "mobile"],
  3: ["countryId", "stateId", "cityId", "address", "businessType"],
  4: ["password", "confirmPassword"],
  5: ["terms"],
};

function summarizeField(value) {
  return String(value || "").trim() || "Not provided";
}

function validateStep(step, values) {
  const errors = {};
  const fields = stepFields[step] || [];

  fields.forEach((field) => {
    const value = values[field];

    if (field === "terms") {
      if (!value) {
        errors[field] = "Please confirm the details are correct to continue.";
      }
      return;
    }

    if (!String(value || "").trim()) {
      errors[field] = "This field is required.";
      return;
    }

    if (field === "email" && !emailPattern.test(String(value).trim())) {
      errors[field] = "Enter a valid email address.";
      return;
    }

    if (field === "mobile" && !mobilePattern.test(String(value).trim())) {
      errors[field] = "Enter a valid mobile number.";
      return;
    }

    if (field === "password" && String(value).trim().length < 8) {
      errors[field] = "Password must be at least 8 characters long.";
      return;
    }

    if (field === "confirmPassword" && value !== values.password) {
      errors[field] = "Passwords do not match.";
    }
  });

  return errors;
}

function fieldWrapperClass(error) {
  return `position-relative${error ? " focus-input" : ""}`;
}

function StepTitle({ step }) {
  return <span className="text text-xs fw-semibold">{stepLabels[step - 1]}</span>;
}

function buildRegistrationPayload(values, emailVerifiedToken) {
  return {
    utp_designation: "Mr.",
    utp_full_name: values.ownerName.trim(),
    utp_mobile: values.mobile.trim(),
    utp_email: values.email.trim(),
    utp_branch_name: values.agencyName.trim(),
    utp_secure_mode: "Secure",
    utp_gst: "",
    utp_agency_phone: values.mobile.trim(),
    utp_agency_pan: "",
    fk_country_id: Number(values.countryId),
    fk_state_id: Number(values.stateId),
    fk_city_id: Number(values.cityId),
    utp_addr_one: values.address.trim(),
    utp_addr_two: "",
    utp_postal_code: "",
    utp_fax_no: "",
    utp_buisness_type: values.businessType.trim(),
    utp_office_space: "Owned",
    utp_system_pswd: values.password,
    utp_billing_address: values.address.trim(),
    utp_billing_contact_person: values.ownerName.trim(),
    utp_billing_email: values.email.trim(),
    utp_billing_mobile: values.mobile.trim(),
    utp_bank_details: "",
    utp_payment_remarks: "",
    confirmPassword: values.confirmPassword,
    emailVerifiedToken,
  };
}

function preferDefaultCountryId(countries) {
  const byId = countries.find((item) => Number(item.country_id) === 101);
  if (byId) return String(byId.country_id);
  const byName = countries.find((item) => String(item.country_name || "").toLowerCase() === "india");
  return byName ? String(byName.country_id) : "";
}

export default function RegisterPage() {
  const [currentStep, setCurrentStep] = useState(1);
  const [values, setValues] = useState(initialValues);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [otpSending, setOtpSending] = useState(false);
  const [otpValue, setOtpValue] = useState("");
  const [otpToken, setOtpToken] = useState("");
  const [otpSeconds, setOtpSeconds] = useState(0);
  const [emailVerifiedToken, setEmailVerifiedToken] = useState("");
  const [otpError, setOtpError] = useState("");
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [locationLoading, setLocationLoading] = useState({
    countries: false,
    states: false,
    cities: false,
  });
  const navigate = useNavigate();

  const wizardState = useMemo(
    () =>
      stepLabels.map((_, index) => {
        const stepNumber = index + 1;
        return {
          number: stepNumber,
          active: stepNumber === currentStep,
          activated: stepNumber < currentStep,
        };
      }),
    [currentStep]
  );

  const selectedCountryName = findLocationLabel(countries, values.countryId, "country_id", "country_name");
  const selectedStateName = findLocationLabel(states, values.stateId, "state_id", "state_name");
  const selectedCityName = findLocationLabel(cities, values.cityId, "city_id", "city_name");
  const canResendOtp = otpSeconds <= 0;

  useEffect(() => {
    if (otpSeconds <= 0) {
      return undefined;
    }
    const timer = window.setInterval(() => {
      setOtpSeconds((current) => Math.max(0, current - 1));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [otpSeconds]);

  useEffect(() => {
    let cancelled = false;

    async function loadCountries() {
      setLocationLoading((current) => ({ ...current, countries: true }));
      try {
        const list = await fetchCountries();
        if (cancelled) return;
        setCountries(list);
        setValues((current) => {
          if (current.countryId) return current;
          const defaultId = preferDefaultCountryId(list);
          return defaultId ? { ...current, countryId: defaultId } : current;
        });
      } catch (error) {
        if (!cancelled) {
          showApiError(error, "LOAD_COUNTRIES");
        }
      } finally {
        if (!cancelled) {
          setLocationLoading((current) => ({ ...current, countries: false }));
        }
      }
    }

    loadCountries();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    if (!values.countryId) {
      setStates([]);
      setCities([]);
      return undefined;
    }

    async function loadStates() {
      setStates([]);
      setCities([]);
      setLocationLoading((current) => ({ ...current, states: true }));
      try {
        const list = await fetchStates(values.countryId);
        if (cancelled) return;
        setStates(list);
      } catch (error) {
        if (!cancelled) {
          setStates([]);
          showApiError(error, "LOAD_STATES");
        }
      } finally {
        if (!cancelled) {
          setLocationLoading((current) => ({ ...current, states: false }));
        }
      }
    }

    loadStates();
    return () => {
      cancelled = true;
    };
  }, [values.countryId]);

  useEffect(() => {
    let cancelled = false;

    if (!values.stateId) {
      setCities([]);
      return undefined;
    }

    async function loadCities() {
      setCities([]);
      setLocationLoading((current) => ({ ...current, cities: true }));
      try {
        const list = await fetchCities(values.stateId);
        if (cancelled) return;
        setCities(list);
      } catch (error) {
        if (!cancelled) {
          setCities([]);
          showApiError(error, "LOAD_CITIES");
        }
      } finally {
        if (!cancelled) {
          setLocationLoading((current) => ({ ...current, cities: false }));
        }
      }
    }

    loadCities();
    return () => {
      cancelled = true;
    };
  }, [values.stateId]);

  function updateValue(name, value) {
    setValues((current) => ({ ...current, [name]: value }));
    setErrors((current) => ({ ...current, [name]: "" }));
  }

  function handleFieldChange(event) {
    const { name, type, value, checked } = event.target;

    if (name === "countryId") {
      setValues((current) => ({ ...current, countryId: value, stateId: "", cityId: "" }));
      setErrors((current) => ({ ...current, countryId: "", stateId: "", cityId: "" }));
      setCities([]);
      setStates([]);
      return;
    }

    if (name === "stateId") {
      setValues((current) => ({ ...current, stateId: value, cityId: "" }));
      setErrors((current) => ({ ...current, stateId: "", cityId: "" }));
      setCities([]);
      return;
    }

    if (name === "email" && emailVerifiedToken) {
      setEmailVerifiedToken("");
      setOtpToken("");
      setOtpValue("");
    }

    updateValue(name, type === "checkbox" ? checked : value);
  }

  async function requestOtp({ resend = false } = {}) {
    setOtpSending(true);
    setOtpError("");
    try {
      const requester = resend ? resendAgentRegistrationOtp : sendAgentRegistrationOtp;
      const result = await requester({
        email: values.email.trim(),
        fullName: values.ownerName.trim(),
        otpToken,
      });
      setOtpToken(result.otpToken || "");
      setOtpSeconds(Number(result.expiresIn || result.resendAvailableIn || 120));
      setOtpValue("");
      showSuccessAction(resend ? "RESEND_OTP" : "SEND_OTP");
      return true;
    } catch (error) {
      const remaining = Number(error?.payload?.data?.remainingSeconds || error?.payload?.remainingSeconds || 0);
      if (remaining > 0) {
        setOtpSeconds(remaining);
      }
      const message = getApiErrorMessage(error, "SEND_OTP");
      setOtpError(message);
      showApiError(error, "SEND_OTP");
      return false;
    } finally {
      setOtpSending(false);
    }
  }

  async function goNext() {
    if (currentStep === 2) {
      return undefined;
    }

    const stepErrors = validateStep(currentStep, values);
    setErrors(stepErrors);
    if (Object.keys(stepErrors).length > 0) {
      showErrorAction("REQUIRED_FIELDS");
      return;
    }

    if (currentStep === 1) {
      if (!emailVerifiedToken) {
        const sent = await requestOtp();
        if (!sent) {
          return;
        }
      }
      setCurrentStep(2);
      return;
    }

    setCurrentStep((step) => Math.min(step + 1, 5));
  }

  function goBack() {
    if (currentStep === 3 && emailVerifiedToken) {
      setCurrentStep(1);
      return;
    }
    setCurrentStep((step) => Math.max(step - 1, 1));
  }

  async function handleVerifyOtp() {
    if (otpValue.length !== 6) {
      setOtpError(getErrorMessage("OTP_REQUIRED"));
      return;
    }
    setOtpSending(true);
    setOtpError("");
    try {
      const result = await verifyAgentRegistrationOtp({
        email: values.email.trim(),
        otp: otpValue,
        otpToken,
      });
      setEmailVerifiedToken(result.emailVerifiedToken || "");
      showSuccessAction("VERIFY_OTP");
      setCurrentStep(3);
    } catch (error) {
      const message = getApiErrorMessage(error, "OTP_INVALID");
      setOtpError(message);
      showApiError(error, "OTP_INVALID");
    } finally {
      setOtpSending(false);
    }
  }

  async function submitRegistration(event) {
    event.preventDefault();
    if (loading) {
      return;
    }

    const stepErrors = validateStep(5, values);
    setErrors(stepErrors);
    if (Object.keys(stepErrors).length > 0) {
      showErrorAction("REQUIRED_REGISTRATION");
      return;
    }

    if (!emailVerifiedToken) {
      showErrorAction("VERIFY_EMAIL_FIRST");
      setCurrentStep(2);
      return;
    }

    setLoading(true);
    try {
      const result = await registerAgent(buildRegistrationPayload(values, emailVerifiedToken));
      showSuccessAction("REGISTER");
      navigate("/waiting-approval", {
        replace: true,
        state: {
          fromRegistration: true,
          partnerId: result?.partnerId || result?.partner?.id,
          email: values.email.trim(),
        },
      });
    } catch (registrationError) {
      showApiError(registrationError, "REGISTER");
    } finally {
      setLoading(false);
    }
  }

  return (
    <AuthLayout
      heroImage="/template/assets/images/auth/agent_register.png"
      heroAlt="Join Way2Umrah Agent Portal"
    >
      {loading ? (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{ background: "rgba(255,255,255,.92)", zIndex: 30 }}
        >
          <div className="text-center px-3">
            <div className="spinner-border text-primary mb-3" role="status" aria-hidden="true" />
            <h5 className="mb-8">Submitting your registration...</h5>
            <p className="mb-0 text-secondary">Please wait while we create your account.</p>
          </div>
        </div>
      ) : null}
      <div className="max-w-768-px mx-auto w-100 auth-form-card">
        <Link to="/login" className="mb-32 max-w-290-px d-inline-block">
          <img src={BRAND.logo} alt={BRAND.name} className="auth-brand-logo" />
        </Link>
        <div className="mb-24">
          <h4 className="mb-12">Register your agency</h4>
          <p className="mb-0 text-secondary-light">
            Complete the essentials, verify your email with OTP, and wait for Way2Umrah approval.
          </p>
        </div>
        <div className="form-wizard">
          <form onSubmit={submitRegistration} noValidate>
            <div className="form-wizard-header overflow-x-auto scroll-sm pb-8 my-24 auth-wizard-steps">
              <ul className="list-unstyled form-wizard-list style-three">
                {wizardState.map((step) => (
                  <li
                    key={step.number}
                    className={`form-wizard-list__item d-flex align-items-center gap-8 ${step.active ? "active" : ""} ${step.activated ? "activated" : ""}`}
                    data-step-item={step.number}
                  >
                    <div className="form-wizard-list__line">
                      <span className="count" data-step-count={step.number}>
                        {step.activated ? <i className="ri-check-line" /> : step.number}
                      </span>
                    </div>
                    <StepTitle step={step.number} />
                  </li>
                ))}
              </ul>
            </div>

            <fieldset className={`wizard-fieldset ${currentStep === 1 ? "show" : ""}`} data-step-panel="1">
              <div className="card border shadow-none">
                <div className="card-body">
                  <div className="row g-3">
                    {[
                      { name: "agencyName", label: "Agency name", type: "text", placeholder: "Way2 Umrah Travels" },
                      { name: "ownerName", label: "Contact person name", type: "text", placeholder: "Ahmad Khan" },
                      { name: "email", label: "Email", type: "email", placeholder: "owner@agency.com" },
                      { name: "mobile", label: "Mobile", type: "tel", placeholder: "98XXXXXXXX" },
                    ].map((field) => (
                      <div className="col-md-6" key={field.name}>
                        <label className="form-label fw-semibold text-primary-light text-sm mb-8" htmlFor={field.name}>
                          {field.label} <span className="text-danger-600">*</span>
                        </label>
                        <div className={fieldWrapperClass(errors[field.name])}>
                          <input
                            type={field.type}
                            id={field.name}
                            name={field.name}
                            className="form-control h-56-px bg-neutral-50 radius-12 wizard-required"
                            placeholder={field.placeholder}
                            value={values[field.name]}
                            onChange={handleFieldChange}
                          />
                          <small className={`text-danger-600 text-xs mt-4 ${errors[field.name] ? "" : "d-none"}`}>
                            {errors[field.name] || ""}
                          </small>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="form-group d-flex flex-wrap align-items-center justify-content-end gap-8 mt-24">
                <Button type="button" className="btn btn-primary-600 px-32 radius-12" onClick={goNext} disabled={otpSending}>
                  {otpSending ? "Sending OTP..." : "Next"}
                </Button>
              </div>
            </fieldset>

            <fieldset className={`wizard-fieldset ${currentStep === 2 ? "show" : ""}`} data-step-panel="2">
              <div className="card border shadow-none">
                <div className="card-body">
                  <h6 className="mb-8">Agent OTP verification</h6>
                  <p className="text-secondary-light mb-16">
                    Enter the 6-digit agent OTP sent to <strong>{values.email || "your email"}</strong>. It expires in 2 minutes.
                  </p>
                  <OtpInputs value={otpValue} onChange={setOtpValue} disabled={otpSending} />
                  <small className={`text-danger-600 text-xs mt-8 ${otpError ? "" : "d-none"}`}>{otpError}</small>
                  <div className="d-flex flex-wrap align-items-center justify-content-between gap-8 mt-16">
                    <div className="text-secondary-light text-sm">
                      {canResendOtp ? "You can resend the OTP now." : `Resend OTP in ${formatOtpCountdown(otpSeconds)}`}
                    </div>
                    <Button
                      type="button"
                      className="btn btn-outline-secondary border-neutral-100 px-24 radius-12"
                      disabled={!canResendOtp || otpSending}
                      onClick={() => requestOtp({ resend: true })}
                    >
                      {otpSending ? "Please wait..." : "Resend OTP"}
                    </Button>
                  </div>
                </div>
              </div>
              <div className="form-group d-flex flex-wrap align-items-center justify-content-end gap-8 mt-24">
                <Button type="button" className="btn btn-outline-secondary border-neutral-100 px-32 radius-12" onClick={goBack}>
                  Back
                </Button>
                <Button type="button" className="btn btn-primary-600 px-32 radius-12" onClick={handleVerifyOtp} disabled={otpSending || otpValue.length !== 6}>
                  {otpSending ? "Verifying..." : "Verify & Continue"}
                </Button>
              </div>
            </fieldset>

            <fieldset className={`wizard-fieldset ${currentStep === 3 ? "show" : ""}`} data-step-panel="3">
              <div className="card border shadow-none">
                <div className="card-body">
                  <div className="row g-3">
                    <div className="col-md-4">
                      <label className="form-label fw-semibold text-primary-light text-sm mb-8" htmlFor="countryId">
                        Country <span className="text-danger-600">*</span>
                      </label>
                      <div className={fieldWrapperClass(errors.countryId)}>
                        <AgentSelect
                          inputId="countryId"
                          name="countryId"
                          size="lg"
                          className="wizard-required"
                          value={values.countryId}
                          options={countries.map((country) => ({
                            value: String(country.country_id),
                            label: country.country_name,
                          }))}
                          onChange={(value) => handleFieldChange({ target: { name: "countryId", value } })}
                          isDisabled={locationLoading.countries}
                          isLoading={locationLoading.countries}
                          placeholder={locationLoading.countries ? "Loading countries..." : "Select country"}
                        />
                        <small className={`text-danger-600 text-xs mt-4 ${errors.countryId ? "" : "d-none"}`}>
                          {errors.countryId || ""}
                        </small>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label fw-semibold text-primary-light text-sm mb-8" htmlFor="stateId">
                        State <span className="text-danger-600">*</span>
                      </label>
                      <div className={fieldWrapperClass(errors.stateId)}>
                        <AgentSelect
                          inputId="stateId"
                          name="stateId"
                          size="lg"
                          className="wizard-required"
                          value={values.stateId}
                          options={states.map((state) => ({
                            value: String(state.state_id),
                            label: state.state_name,
                          }))}
                          onChange={(value) => handleFieldChange({ target: { name: "stateId", value } })}
                          isDisabled={!values.countryId || locationLoading.states}
                          isLoading={locationLoading.states}
                          placeholder={
                            !values.countryId
                              ? "Select country first"
                              : locationLoading.states
                                ? "Loading states..."
                                : "Select state"
                          }
                        />
                        <small className={`text-danger-600 text-xs mt-4 ${errors.stateId ? "" : "d-none"}`}>
                          {errors.stateId || ""}
                        </small>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label fw-semibold text-primary-light text-sm mb-8" htmlFor="cityId">
                        City <span className="text-danger-600">*</span>
                      </label>
                      <div className={fieldWrapperClass(errors.cityId)}>
                        <AgentSelect
                          inputId="cityId"
                          name="cityId"
                          size="lg"
                          className="wizard-required"
                          value={values.cityId}
                          options={cities.map((city) => ({
                            value: String(city.city_id),
                            label: city.city_name,
                          }))}
                          onChange={(value) => handleFieldChange({ target: { name: "cityId", value } })}
                          isDisabled={!values.stateId || locationLoading.cities}
                          isLoading={locationLoading.cities}
                          placeholder={
                            !values.stateId
                              ? "Select state first"
                              : locationLoading.cities
                                ? "Loading cities..."
                                : "Select city"
                          }
                        />
                        <small className={`text-danger-600 text-xs mt-4 ${errors.cityId ? "" : "d-none"}`}>
                          {errors.cityId || ""}
                        </small>
                      </div>
                    </div>
                    <div className="col-12">
                      <label className="form-label fw-semibold text-primary-light text-sm mb-8" htmlFor="address">
                        Business address <span className="text-danger-600">*</span>
                      </label>
                      <div className={fieldWrapperClass(errors.address)}>
                        <textarea
                          id="address"
                          name="address"
                          className="form-control radius-12 wizard-required"
                          rows="3"
                          placeholder="Office address"
                          value={values.address}
                          onChange={handleFieldChange}
                        />
                        <small className={`text-danger-600 text-xs mt-4 ${errors.address ? "" : "d-none"}`}>
                          {errors.address || ""}
                        </small>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-semibold text-primary-light text-sm mb-8" htmlFor="businessType">
                        Business type <span className="text-danger-600">*</span>
                      </label>
                      <div className={fieldWrapperClass(errors.businessType)}>
                        <AgentSelect
                          inputId="businessType"
                          name="businessType"
                          size="lg"
                          className="wizard-required"
                          value={values.businessType}
                          options={BUSINESS_TYPES}
                          onChange={(value) => handleFieldChange({ target: { name: "businessType", value } })}
                          isSearchable={false}
                          placeholder="Select business type"
                        />
                        <small className={`text-danger-600 text-xs mt-4 ${errors.businessType ? "" : "d-none"}`}>
                          {errors.businessType || ""}
                        </small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="form-group d-flex flex-wrap align-items-center justify-content-end gap-8 mt-24">
                <Button type="button" className="btn btn-outline-secondary border-neutral-100 px-32 radius-12" onClick={goBack}>
                  Back
                </Button>
                <Button type="button" className="btn btn-primary-600 px-32 radius-12" onClick={goNext}>
                  Next
                </Button>
              </div>
            </fieldset>

            <fieldset className={`wizard-fieldset ${currentStep === 4 ? "show" : ""}`} data-step-panel="4">
              <div className="card border shadow-none">
                <div className="card-body">
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label fw-semibold text-primary-light text-sm mb-8" htmlFor="password">
                        Password <span className="text-danger-600">*</span>
                      </label>
                      <div className={fieldWrapperClass(errors.password)}>
                        <input
                          type="password"
                          id="password"
                          name="password"
                          className="form-control h-56-px bg-neutral-50 radius-12 wizard-required"
                          placeholder="Minimum 8 characters"
                          value={values.password}
                          onChange={handleFieldChange}
                        />
                        <small className={`text-danger-600 text-xs mt-4 ${errors.password ? "" : "d-none"}`}>
                          {errors.password || ""}
                        </small>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-semibold text-primary-light text-sm mb-8" htmlFor="confirmPassword">
                        Confirm password <span className="text-danger-600">*</span>
                      </label>
                      <div className={fieldWrapperClass(errors.confirmPassword)}>
                        <input
                          type="password"
                          id="confirmPassword"
                          name="confirmPassword"
                          className="form-control h-56-px bg-neutral-50 radius-12 wizard-required"
                          placeholder="Repeat your password"
                          value={values.confirmPassword}
                          onChange={handleFieldChange}
                        />
                        <small className={`text-danger-600 text-xs mt-4 ${errors.confirmPassword ? "" : "d-none"}`}>
                          {errors.confirmPassword || ""}
                        </small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="form-group d-flex flex-wrap align-items-center justify-content-end gap-8 mt-24">
                <Button type="button" className="btn btn-outline-secondary border-neutral-100 px-32 radius-12" onClick={goBack}>
                  Back
                </Button>
                <Button type="button" className="btn btn-primary-600 px-32 radius-12" onClick={goNext}>
                  Next
                </Button>
              </div>
            </fieldset>

            <fieldset className={`wizard-fieldset ${currentStep === 5 ? "show" : ""}`} data-step-panel="5">
              <div className="card border shadow-none">
                <div className="card-body">
                  <div className="row g-3">
                    <div className="col-md-6">
                      <div className="p-3 bg-neutral-50 radius-12 h-100">
                        <div className="text-uppercase text-secondary small fw-semibold mb-2">Agency</div>
                        <div className="fw-semibold">{summarizeField(values.agencyName)}</div>
                        <div className="text-secondary small mt-1">{summarizeField(values.ownerName)}</div>
                        <button type="button" className="btn btn-link px-0 mt-8" onClick={() => setCurrentStep(1)}>
                          Edit
                        </button>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="p-3 bg-neutral-50 radius-12 h-100">
                        <div className="text-uppercase text-secondary small fw-semibold mb-2">Contact</div>
                        <div className="fw-semibold">{summarizeField(values.email)}</div>
                        <div className="text-secondary small mt-1">{summarizeField(values.mobile)}</div>
                        <div className="text-success small mt-1">Email OTP verified</div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="p-3 bg-neutral-50 radius-12 h-100">
                        <div className="text-uppercase text-secondary small fw-semibold mb-2">Location</div>
                        <div className="fw-semibold">
                          {summarizeField(selectedCityName)}, {summarizeField(selectedStateName)}
                        </div>
                        <div className="text-secondary small mt-1">{summarizeField(selectedCountryName)}</div>
                        <div className="text-secondary small mt-1">{summarizeField(values.address)}</div>
                        <button type="button" className="btn btn-link px-0 mt-8" onClick={() => setCurrentStep(3)}>
                          Edit
                        </button>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="p-3 bg-neutral-50 radius-12 h-100">
                        <div className="text-uppercase text-secondary small fw-semibold mb-2">Business</div>
                        <div className="fw-semibold">{summarizeField(values.businessType)}</div>
                        <div className="text-secondary small mt-1">Password: ••••••••</div>
                        <button type="button" className="btn btn-link px-0 mt-8" onClick={() => setCurrentStep(4)}>
                          Edit
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="form-check style-check d-flex align-items-start mt-24">
                <input
                  className="form-check-input border border-neutral-300 wizard-required"
                  type="checkbox"
                  id="terms"
                  name="terms"
                  checked={values.terms}
                  onChange={handleFieldChange}
                />
                <label className="form-check-label" htmlFor="terms">
                  I confirm the details are correct and accept the agency terms.
                </label>
              </div>
              <small className={`text-danger-600 text-xs mt-4 ${errors.terms ? "" : "d-none"}`} id="terms-error">
                {errors.terms || ""}
              </small>
              <div className="form-group d-flex flex-wrap align-items-center justify-content-end gap-8 mt-24">
                <Button type="button" className="btn btn-outline-secondary border-neutral-100 px-32 radius-12" onClick={goBack}>
                  Back
                </Button>
                <Button type="submit" className="btn btn-primary-600 px-32 radius-12" disabled={loading}>
                  {loading ? "Submitting..." : "Submit Registration"}
                </Button>
              </div>
            </fieldset>
            <div className="mt-24 text-sm">
              <p className="mb-0">
                Already have an agency account? <Link to="/login" className="text-primary-600 fw-semibold">Sign In</Link>
              </p>
            </div>
          </form>
        </div>
      </div>
    </AuthLayout>
  );
}
